import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Check,
  CheckCheck,
  Copy,
  Loader2,
  Rocket,
  Terminal,
  AlertCircle,
  X,
} from 'lucide-react';
import { api, ApiError } from '../api/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/Dialog';
import { Button } from '../components/ui/Button';
import { cloneCommand } from '../lib/eject';
import { cn } from '../lib/cn';

interface Props {
  projectId: string;
  open: boolean;
  onClose: () => void;
  /** Called once the eject completes (repo URL known) so the parent can refresh. */
  onEjected?: () => void;
}

/** Small copy-to-clipboard icon button with a transient ✓ confirmation. */
function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label ?? 'Copy'}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* noop — clipboard may be blocked; the value is still visible to copy by hand */
        }
      }}
      className={cn('shrink-0', copied && 'text-emerald-600')}
    >
      {copied ? <CheckCheck className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
    </Button>
  );
}

type Step = 'confirm' | 'ejecting' | 'ejected' | 'error';

/**
 * Standalone "Publish & Eject" flow — decoupled from custom domains. Works on a
 * project's default subdomain baseline (the eject only needs a live deployment),
 * and a custom domain can be attached before or after. The eject is a one-way
 * hard-stop (the Factory stops managing the site), so it's an explicit action
 * behind a confirm screen.
 */
export function EjectDialog({ projectId, open, onClose, onEjected }: Props) {
  const [step, setStep] = useState<Step>('confirm');
  const [ejectError, setEjectError] = useState<string | null>(null);
  // Guards the eject POST to exactly one fire per dialog session.
  const ejectFiredRef = useRef(false);

  // Poll /state: a single fetch on open (to detect an already-ejected project),
  // then 2s polling only while we're waiting for the worker to publish the repo.
  const { data: projectState } = useQuery({
    queryKey: ['projectState', projectId],
    queryFn: () => api.getProjectState(projectId),
    enabled: open,
    refetchInterval: (q) => {
      if (step !== 'ejecting') return false;
      return q.state.data?.project.ejectedRepoUrl ? false : 2000;
    },
  });

  const ejectedRepoUrl = projectState?.project.ejectedRepoUrl;

  async function startEject(): Promise<void> {
    if (ejectFiredRef.current) return;
    ejectFiredRef.current = true;
    setEjectError(null);
    setStep('ejecting');
    try {
      await api.ejectProject(projectId);
      // success — the /state poll surfaces ejectedRepoUrl, which drives 'ejected'.
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : String(err);
      // Already ejected: the repo exists; the poll will surface its URL.
      if (/already ejected/i.test(msg)) return;
      setEjectError(msg);
      setStep('error');
    }
  }

  // If the project is already ejected when the dialog opens, jump to the clone screen.
  useEffect(() => {
    if (open && step === 'confirm' && ejectedRepoUrl) setStep('ejected');
  }, [open, step, ejectedRepoUrl]);

  // The worker finished: the repo URL is now on the project → show clone screen.
  useEffect(() => {
    if (step === 'ejecting' && ejectedRepoUrl) {
      setStep('ejected');
      onEjected?.();
    }
  }, [step, ejectedRepoUrl, onEjected]);

  function handleOpenChange(next: boolean): void {
    if (!next) {
      setStep('confirm');
      setEjectError(null);
      ejectFiredRef.current = false;
      onClose();
    }
  }

  function retry(): void {
    ejectFiredRef.current = false;
    setEjectError(null);
    void startEject();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        {step === 'confirm' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-9 w-9 rounded-full bg-zinc-900 flex items-center justify-center">
                  <Rocket className="h-4 w-4 text-white" />
                </div>
                <DialogTitle>Publish &amp; Eject</DialogTitle>
              </div>
              <DialogDescription>
                Hand this site off to your workspace as a standalone GitHub
                repository you manage with your AI agent. It works on the site's
                current URL — you can attach a custom domain anytime, before or
                after.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-[12.5px] leading-relaxed text-amber-900">
              <span className="font-medium">This is a one-way handoff.</span> The
              Factory stops managing this site after you eject — you take full
              control of the repo. (You can still attach a custom domain to it
              later.)
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Not now
              </Button>
              <Button onClick={() => void startEject()} leftIcon={<Rocket className="h-3.5 w-3.5" />}>
                Publish &amp; Eject
              </Button>
            </DialogFooter>
          </>
        )}

        {step === 'ejecting' && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              aria-label="Close (the hand-off will continue in the background)"
            >
              <X className="h-4 w-4" />
            </button>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-9 w-9 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 text-blue-700 animate-spin" />
                </div>
                <DialogTitle>Finalizing &amp; handing off your site…</DialogTitle>
              </div>
              <DialogDescription>
                We're packaging this site into your own standalone GitHub
                repository — creating the repo, pushing the code, and wiring up
                automated deploys. This takes a few seconds.
              </DialogDescription>
            </DialogHeader>
          </>
        )}

        {step === 'ejected' && ejectedRepoUrl && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-9 w-9 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center">
                  <Check className="h-4 w-4 text-emerald-700" />
                </div>
                <DialogTitle>Site Ready &amp; Ejected</DialogTitle>
              </div>
              <DialogDescription>
                This site is now your own standalone repository. Clone it locally
                to manage it with your AI agent.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <div className="text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                  Repository
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
                  <a
                    href={ejectedRepoUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-mono text-[13px] text-zinc-800 hover:text-zinc-950 hover:underline flex-1 truncate"
                  >
                    {ejectedRepoUrl}
                  </a>
                  <CopyButton text={ejectedRepoUrl} label="Copy repository URL" />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                  <Terminal className="h-3 w-3" />
                  Set up locally
                </div>
                <div className="flex items-start gap-2 rounded-lg border border-zinc-200 bg-zinc-950 px-3 py-2.5">
                  <code className="font-mono text-[12.5px] leading-relaxed text-zinc-100 flex-1 whitespace-pre-wrap break-all">
                    {cloneCommand(ejectedRepoUrl)}
                  </code>
                  <CopyButton
                    text={cloneCommand(ejectedRepoUrl)}
                    label="Copy setup command"
                  />
                </div>
                <p className="text-[12px] text-zinc-500">
                  Run this in your terminal to clone the repo into your workspace
                  and install dependencies.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button asChild variant="outline">
                <a href={ejectedRepoUrl} target="_blank" rel="noreferrer">
                  View on GitHub
                </a>
              </Button>
              <Button onClick={onClose}>Done</Button>
            </DialogFooter>
          </>
        )}

        {step === 'error' && (
          <>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-900 transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-1">
                <div className="h-9 w-9 rounded-full bg-red-50 border border-red-200 flex items-center justify-center">
                  <AlertCircle className="h-4 w-4 text-red-700" />
                </div>
                <DialogTitle>Eject didn't start</DialogTitle>
              </div>
              <DialogDescription>
                We couldn't begin the hand-off. The engine recorded this reason:
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-[13px] text-red-800 font-mono whitespace-pre-wrap break-words">
              {ejectError ?? 'Unknown error — check the engine logs.'}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button onClick={retry}>Try again</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
